import { useGetProposal } from '@/api/queries/proposal/useGetProposal';
import { usePermissions } from '@/hooks/usePermissions';
import PopupBlockedModal from '@/ui/components/clientPortalPreview/PopupBlockedModal';
import { useClientPortalPreview } from '@/ui/components/clientPortalPreview/useClientPortalPreview';
import {
  PanelBody,
  PanelHeaderBar,
} from '@/ui/components/estimatesAndProposals/draftSendEmail/panelLayout';
import ProposalEditorSkeleton from '@/ui/pages/proposalBuilderPage/components/ProposalEditorSkeleton';
import ProposalFinancials from '@/ui/pages/proposalBuilderPage/components/ProposalFinancials';
import ProposalHeaderIdentity from '@/ui/pages/proposalBuilderPage/components/ProposalHeaderIdentity';
import ProposalLifecycleModals from '@/ui/pages/proposalBuilderPage/components/proposalLifecycleActions/ProposalLifecycleModals';
import { useProposalLifecycleActions } from '@/ui/pages/proposalBuilderPage/components/proposalLifecycleActions/useProposalLifecycleActions';
import SendProposalView from '@/ui/pages/proposalBuilderPage/components/SendProposalView';
import { useDownloadProposalPdf } from '@/ui/pages/proposalBuilderPage/components/useDownloadProposalPdf';
import { useDuplicateProposal } from '@/ui/pages/proposalBuilderPage/components/useDuplicateProposal';
import { LabourForecastPermissions } from '@payaca/permissions/labourForecast/labourForecast.permissions';
import { MaterialsListPermissions } from '@payaca/permissions/materialsList/materialsList.permissions';
import { FC, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  buildSentProposalActions,
  SentProposalActionKey,
} from './sentProposalActions';
import SentProposalActionsList from './SentProposalActionsList';
import { getSentProposalStatus } from './sentProposalStatus';
import SentProposalStatusSummary from './SentProposalStatusSummary';

/** Router location state that opens the panel on its resend composer. */
export type SentProposalPanelLocationState = { view?: 'resend' };

type SentProposalPanelProps = {
  /** Public id of the Proposal — the route param. */
  proposalId: string;
  /** Public id of the parent Project — the route param. */
  projectId: string;
};

/**
 * The builder's right panel once the Proposal has been sent: its actions, and
 * the resend composer one of them swaps in.
 *
 * The view is local state rather than a route because the composer holds an
 * unsent email draft, which a route would let the back button discard silently.
 */
const SentProposalPanel: FC<SentProposalPanelProps> = (props) => {
  const { proposalId, projectId } = props;

  const { proposal } = useGetProposal(proposalId);

  const location = useLocation<SentProposalPanelLocationState | undefined>();
  const [view, setView] = useState<'actions' | 'resend'>(
    location.state?.view ?? 'actions'
  );
  // Held above both views so the summary keeps its expanded state across the
  // swap.
  const [financialsOpen, setFinancialsOpen] = useState(false);

  const { userHasRequiredPermission } = usePermissions();
  const canSeeMaterialsList = userHasRequiredPermission([
    MaterialsListPermissions.GET_MATERIALS_LISTS,
  ]);
  const canSeeLabourForecast = userHasRequiredPermission([
    LabourForecastPermissions.GET_LABOUR_FORECAST,
  ]);

  const lifecycle = useProposalLifecycleActions({ proposalId, projectId });
  const pdf = useDownloadProposalPdf(proposalId);
  const duplicate = useDuplicateProposal({ proposalId, projectId });
  // Called once: a second call would mint a second preview and stand up a
  // second pop-up-blocked modal.
  const preview = useClientPortalPreview(projectId);

  // Swapping the view unmounts whatever was clicked, which would drop focus to
  // the document body. Focus the view that replaced it instead.
  const viewRef = useRef<HTMLDivElement>(null);
  const hasSwappedView = useRef(false);
  useEffect(() => {
    if (hasSwappedView.current) viewRef.current?.focus();
    hasSwappedView.current = true;
  }, [view]);

  const status = getSentProposalStatus(proposal);

  let processingKey: SentProposalActionKey | null = null;
  if (pdf.isDownloading) processingKey = 'downloadPdf';
  else if (preview.isPending) processingKey = 'previewAsCustomer';

  // Unreachable in practice — the builder only mounts this panel for a sent
  // Proposal, and waits on the query before it does.
  if (!status) return <ProposalEditorSkeleton />;

  return (
    <>
      <div ref={viewRef} tabIndex={-1} className="h-full focus:outline-none">
        {view === 'resend' ? (
          <SendProposalView
            back={{
              onClick: () => setView('actions'),
              label: 'Back to actions',
            }}
            financials={{
              open: financialsOpen,
              onOpenChange: setFinancialsOpen,
            }}
          />
        ) : (
          <div className="flex h-full min-h-0 flex-col">
            <PanelHeaderBar>
              <PanelHeaderBar.Group className="min-w-0">
                <ProposalHeaderIdentity proposalId={proposalId} />
              </PanelHeaderBar.Group>
            </PanelHeaderBar>

            <PanelBody>
              <ProposalFinancials
                proposalId={proposalId}
                projectId={projectId}
                editMaterialsTo={
                  canSeeMaterialsList
                    ? `/projects/${projectId}/materials`
                    : undefined
                }
                editLabourTo={
                  canSeeLabourForecast
                    ? `/projects/${projectId}/labour`
                    : undefined
                }
                defaultOpen={financialsOpen}
                onOpenChange={setFinancialsOpen}
              />

              <SentProposalStatusSummary
                proposalId={proposalId}
                status={status}
              />

              <SentProposalActionsList
                actions={buildSentProposalActions({
                  lifecycleActions: lifecycle.actions,
                  availability: proposal?.actionAvailability,
                  canDuplicate: duplicate.canDuplicate,
                  canPreviewAsCustomer: preview.canPreview,
                  canDownloadPdf: pdf.canDownload,
                  isDownloadingPdf: pdf.isDownloading,
                  previewBlockedReasons: preview.unavailableReasons,
                  onResend: () => setView('resend'),
                  onDuplicate: duplicate.openDuplicate,
                  // `isBlocked` also covers a preview already in flight, which
                  // the row's disabled state does not.
                  onPreviewAsCustomer: () => {
                    if (!preview.isBlocked) preview.openPreview();
                  },
                  onDownloadPdf: pdf.downloadPdf,
                })}
                processingKey={processingKey}
              />
            </PanelBody>
          </div>
        )}
      </div>

      <ProposalLifecycleModals
        proposalId={proposalId}
        projectId={projectId}
        openConfirmation={lifecycle.openConfirmation}
        onCloseConfirmation={lifecycle.closeConfirmation}
      />

      {duplicate.modal}

      <PopupBlockedModal
        url={preview.blockedPreviewUrl}
        onClose={preview.closeBlockedPreview}
      />
    </>
  );
};

export default SentProposalPanel;
